/**
 * shapes.ts — world generators (the "day themes").
 *
 * Direct port of the Python generators in `_proto_reflex_v1.py`
 * (make_circles / make_squares / make_blobs / make_spirals / make_blended,
 * lines 124-200 + 659-680). Each returns a flat Int8Array of length GRID*GRID
 * with values in {FREE, GOLD, STONE}, matching mob's `World.cells` layout
 * (flat row-major).
 *
 * Gold arrangements, by shape:
 *   circles — gold in rings, stones scattered inside each ring
 *   squares — gold on square perimeters, a stone at each center
 *   blobs   — shapeless clustered gold (the mob 'clustered' shape)
 *   spirals — gold along an Archimedean spiral path
 */

import {
  GRID,
  START_R,
  START_C,
  FREE,
  GOLD,
  STONE,
  type ShapeName,
} from './constants';

/** A grid is a flat Int8Array of length GRID*GRID (row-major). */
export type Grid = Int8Array;

export function newGrid(): Grid {
  return new Int8Array(GRID * GRID).fill(FREE);
}

/** Indexed getter (row, col) -> token. */
export function cellAt(g: Grid, r: number, c: number): number {
  return g[r * GRID + c];
}

/** Indexed setter (row, col) -> token. Mutates in place. */
export function setCell(g: Grid, r: number, c: number, v: number): void {
  g[r * GRID + c] = v;
}

/** Count of a given token value in the grid. */
export function countToken(g: Grid, value: number): number {
  let n = 0;
  for (let i = 0; i < g.length; i++) if (g[i] === value) n++;
  return n;
}

/** Deep copy. */
export function cloneGrid(g: Grid): Grid {
  return Int8Array.from(g);
}

// ── Generators ────────────────────────────────────────────────────────

/** Gold arranged in rings (the barrier-free circle outline). */
export function makeCircles(rng: () => number, nCircles = 3, radius = 2): Grid {
  const g = newGrid();
  for (let i = 0; i < nCircles; i++) {
    const cr = Math.floor(rng() * (GRID - 2 * radius - 2) + (radius + 1));
    const cc = Math.floor(rng() * (GRID - 2 * radius - 2) + (radius + 1));
    // Bresenham-ish ring of step ~max(15°, 45°/radius).
    const step = Math.max(15, Math.floor(45 / Math.max(1, radius)));
    for (let deg = 0; deg < 360; deg += step) {
      const a = (deg * Math.PI) / 180;
      const r = Math.round(cr + radius * Math.cos(a));
      const c = Math.round(cc + radius * Math.sin(a));
      if (r >= 0 && r < GRID && c >= 0 && c < GRID) g[r * GRID + c] = GOLD;
    }
    // Scatter a few stones inside the ring (barriers).
    for (let s = 0; s < radius; s++) {
      const sr = cr + Math.floor(rng() * (2 * radius - 1) - (radius - 1));
      const sc = cc + Math.floor(rng() * (2 * radius - 1) - (radius - 1));
      if (sr >= 0 && sr < GRID && sc >= 0 && sc < GRID) g[sr * GRID + sc] = STONE;
    }
  }
  g[START_R * GRID + START_C] = FREE;
  return g;
}

/** Gold on the PERIMETER of axis-aligned squares (filled corners). */
export function makeSquares(rng: () => number, nSquares = 3, half = 2): Grid {
  const g = newGrid();
  for (let i = 0; i < nSquares; i++) {
    const cr = Math.floor(rng() * (GRID - 2 * half - 2) + (half + 1));
    const cc = Math.floor(rng() * (GRID - 2 * half - 2) + (half + 1));
    for (let dr = -half; dr <= half; dr++) {
      for (let dc = -half; dc <= half; dc++) {
        if (Math.abs(dr) === half || Math.abs(dc) === half) {
          const r = cr + dr;
          const c = cc + dc;
          if (r >= 0 && r < GRID && c >= 0 && c < GRID) g[r * GRID + c] = GOLD;
        }
      }
    }
    g[cr * GRID + cc] = STONE; // a stone at the center (barrier to evade)
  }
  g[START_R * GRID + START_C] = FREE;
  return g;
}

/** Shapeless clustered gold (the mob-queen 'clustered' shape). */
export function makeBlobs(rng: () => number, nBlobs = 4, spread = 2, goldP = 0.55): Grid {
  const g = newGrid();
  const centers: Array<[number, number]> = [];
  for (let i = 0; i < nBlobs; i++) {
    centers.push([Math.floor(rng() * (GRID - 8) + 4), Math.floor(rng() * (GRID - 8) + 4)]);
  }
  for (const [cr, cc] of centers) {
    for (let dr = -spread; dr <= spread; dr++) {
      for (let dc = -spread; dc <= spread; dc++) {
        const nr = cr + dr;
        const nc = cc + dc;
        if (nr >= 0 && nr < GRID && nc >= 0 && nc < GRID && rng() < goldP) {
          g[nr * GRID + nc] = GOLD;
        }
      }
    }
  }
  // Clear a 3×3 patch around the start so the agent begins on FREE.
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      g[(START_R + dr) * GRID + (START_C + dc)] = FREE;
    }
  }
  return g;
}

/** Gold arranged along an Archimedean spiral path. */
export function makeSpirals(rng: () => number, turns = 2, goldEvery = 2): Grid {
  void rng;
  void turns;
  const g = newGrid();
  const cr = Math.floor(GRID / 2);
  const cc = Math.floor(GRID / 2);
  let theta = 0;
  let r = 0.5;
  let count = 0;
  const visited = new Set<number>();
  while (r < GRID * 0.45) {
    const rr = Math.round(cr + r * Math.cos(theta));
    const cc2 = Math.round(cc + r * Math.sin(theta));
    const key = rr * GRID + cc2;
    if (!visited.has(key) && rr >= 0 && rr < GRID && cc2 >= 0 && cc2 < GRID) {
      visited.add(key);
      if (count % goldEvery === 0) g[rr * GRID + cc2] = GOLD;
      count++;
    }
    theta += 0.4;
    r += 0.15;
  }
  g[START_R * GRID + START_C] = FREE;
  return g;
}

export const SHAPES: Record<ShapeName, (rng: () => number) => Grid> = {
  circles: (rng) => makeCircles(rng),
  squares: (rng) => makeSquares(rng),
  blobs: (rng) => makeBlobs(rng),
  spirals: (rng) => makeSpirals(rng),
};

/**
 * Blend two shape grids by alpha (0 = pure A, 1 = pure B). Where they agree,
 * keep it; where they differ, take B's value with probability alpha. Keeps
 * gold density smooth across the morph. Ported from make_blended (lines 659).
 */
export function makeBlended(
  rng: () => number,
  shapeA: ShapeName,
  shapeB: ShapeName,
  alpha: number,
): Grid {
  const ga = SHAPES[shapeA](rng);
  const gb = SHAPES[shapeB](rng);
  const out = cloneGrid(ga);
  for (let i = 0; i < out.length; i++) {
    if (ga[i] !== gb[i]) {
      // Differ: take B with prob alpha, else A.
      out[i] = rng() < alpha ? gb[i] : ga[i];
    }
  }
  out[START_R * GRID + START_C] = FREE;
  return out;
}
